import { getItem, type ItemInstance, type ItemTemplate } from '@shefira/shared';

/**
 * Which healing item the quick-potion slot drinks. Presentation only: the
 * client picks an item id for the `use_item` intent the bag already sends,
 * and the server still checks and applies it (CLAUDE.md rule 1). It lives in
 * the client because nothing on the server asks this question.
 */

export interface PotionChoice {
  /** The stack to drink from: the first one in the bag of the chosen kind. */
  itemId: string;
  template: ItemTemplate;
  /** How many of that kind are left in the bag, every stack counted. */
  count: number;
}

/** A consumable that heals; a bait (which heals nothing) never counts. */
export function healsOnUse(template: ItemTemplate | undefined): template is ItemTemplate & {
  heal: number;
} {
  return (
    template?.kind === 'consumable' &&
    template.bait === undefined &&
    template.heal !== undefined &&
    template.heal > 0
  );
}

/**
 * A potion rather than food: every flask, the merchant's and the alchemist's,
 * wears a `icon.potion…` icon. The slot is the potion's; bread only stands in
 * when the bag has no potion at all.
 */
export function isPotion(template: ItemTemplate): boolean {
  return template.icon.startsWith('icon.potion');
}

/**
 * Among potions (food only when there is none), the smallest heal that
 * covers the missing health, so nothing strong is wasted on a scratch; when
 * none covers it, the strongest one.
 */
export function choosePotion(
  bag: readonly { slot: number; item: ItemInstance }[],
  missing: number,
  lookup: (templateId: string) => ItemTemplate | undefined = getItem,
): PotionChoice | undefined {
  const kinds = new Map<string, PotionChoice & { heal: number; slot: number }>();
  for (const { slot, item } of bag) {
    const template = lookup(item.templateId);
    if (!healsOnUse(template)) continue;
    const known = kinds.get(item.templateId);
    if (known === undefined) {
      kinds.set(item.templateId, {
        itemId: item.id,
        template,
        count: item.quantity,
        heal: template.heal,
        slot,
      });
      continue;
    }
    known.count += item.quantity;
    if (slot < known.slot) {
      known.slot = slot;
      known.itemId = item.id;
    }
  }
  const found = [...kinds.values()];
  const potions = found.filter((kind) => isPotion(kind.template));
  const all = potions.length > 0 ? potions : found;
  if (all.length === 0) return undefined;
  const enough = all.filter((kind) => kind.heal >= missing);
  const best =
    enough.length > 0
      ? enough.reduce((a, b) => (b.heal < a.heal || (b.heal === a.heal && b.slot < a.slot) ? b : a))
      : all.reduce((a, b) => (b.heal > a.heal || (b.heal === a.heal && b.slot < a.slot) ? b : a));
  return { itemId: best.itemId, template: best.template, count: best.count };
}
